import React, { useState } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useRBAC } from '../../contexts/RBACContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import TicketGeneration from './components/TicketGeneration';
import TicketValidation from './components/TicketValidation';
import MealTicketReports from './components/MealTicketReports';
import Cardholders from './components/Cardholders';
import MealSettings from './components/MealSettings';

const TABS = [
  { id: 'validation', label: 'Validate Tickets', icon: 'ScanLine', adminOnly: false },
  { id: 'generate', label: 'Generate Tickets', icon: 'Ticket', adminOnly: true },
  { id: 'cardholders', label: 'Cardholders', icon: 'Users', adminOnly: true },
  { id: 'reports', label: 'Reports & Analytics', icon: 'BarChart3', adminOnly: false },
  { id: 'settings', label: 'Meal Settings', icon: 'Settings2', adminOnly: true },
];

export default function MealTicketSystem() {
  const { isAdmin, isViewer, role } = useRBAC();
  const realtime = useRealtime();
  const [activeTab, setActiveTab] = useState('validation');

  const canAccess = isAdmin || isViewer || role === 'restaurant_moderator';
  const visibleTabs = TABS?.filter(t => !t?.adminOnly || isAdmin);

  const { totalServedToday, activeTickets } = realtime?.mealMetrics || {};

  if (!canAccess) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <Icon name="ShieldX" size={28} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Access Restricted</h2>
          <p className="text-muted-foreground text-sm">You do not have permission to access the Meal Ticket System.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground font-heading flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                <Icon name="Ticket" size={20} className="text-amber-600" />
              </div>
              Meal Ticket System
            </h2>
            <p className="text-sm text-muted-foreground mt-1 ml-12">
              Generate, validate, and manage meal tickets for Breakfast, Lunch &amp; Dinner
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Live sync indicator */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live · {totalServedToday ?? 0} served today
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card border border-border rounded-lg px-3 py-2">
              <Icon name="Shield" size={14} className={isAdmin ? 'text-amber-500' : 'text-sky-400'} />
              <span className={`font-semibold ${isAdmin ? 'text-amber-600' : 'text-sky-600'}`}>
                {isAdmin ? 'Full Admin' : 'Moderator'}
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 w-fit overflow-x-auto">
          {visibleTabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeTab === tab?.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'validation' && <TicketValidation />}
        {activeTab === 'generate' && isAdmin && <TicketGeneration />}
        {activeTab === 'cardholders' && isAdmin && <Cardholders />}
        {activeTab === 'reports' && <MealTicketReports />}
        {activeTab === 'settings' && isAdmin && <MealSettings />}
      </div>
    </MainLayout>
  );
}
